import { useParams, Navigate } from "react-router-dom";
import { Star, Clock, Check } from "lucide-react";
import { getTicket } from "@/data/tickets";
import { PageHero } from "@/components/common/PageHero";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { FeatureCard } from "@/components/cards/FeatureCard";
import { FAQAccordion } from "@/components/blocks/FAQAccordion";
import { StickyBookingCard } from "@/components/blocks/StickyBookingCard";
import { RelatedTickets } from "@/components/cards/RelatedTickets";
import { RelatedLinks } from "@/components/cards/RelatedLinks";
import { CTASection } from "@/components/common/CTASection";
import { AlertBox } from "@/components/common/AlertBox";

const Section = ({ children, className = "" }) => (
  <Reveal className={`scroll-mt-24 ${className}`}>{children}</Reveal>
);

export default function TicketDetail() {
  const { slug } = useParams();
  const ticket = getTicket(slug);
  if (!ticket) return <Navigate to="/tickets" replace />;

  const meta = (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/85">
      <span className="flex items-center gap-1.5">
        <Star size={15} className="fill-gold text-gold" />
        <strong className="font-medium">{ticket.rating}</strong>
        <span className="text-white/60">({ticket.reviews.toLocaleString()})</span>
      </span>
      <span className="flex items-center gap-1.5">
        <Clock size={15} className="text-gold-light" /> {ticket.duration}
      </span>
      <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium">
        From €{ticket.price}
      </span>
    </div>
  );

  return (
    <>
      <PageHero
        overline={ticket.accent}
        title={ticket.name}
        subtitle={ticket.tagline}
        image={ticket.heroImage}
        meta={meta}
        breadcrumbs={[
          { label: "Tickets", to: "/tickets" },
          { label: ticket.shortName || ticket.name },
        ]}
      />

      <div className="container-wide py-14 md:py-20">
        <div className="grid gap-12 lg:grid-cols-[1fr_360px]">
          {/* Main content */}
          <div className="space-y-16 md:space-y-20">
            <Section>
              <SectionHeading overline="Ticket overview" title={`About the ${ticket.shortName || ticket.name}`} />
              <div className="mt-6 space-y-4">
                {ticket.overview.map((p, i) => (
                  <p key={i} className="text-base md:text-lg leading-relaxed font-light text-slatey">
                    {p}
                  </p>
                ))}
              </div>
            </Section>

            <Section>
              <SectionHeading overline="Why choose this ticket" title="What makes it worthwhile" />
              <div className="mt-8 grid gap-5 sm:grid-cols-3">
                {ticket.whyChoose.map((w, i) => (
                  <FeatureCard key={i} icon={w.icon} title={w.title} text={w.text} />
                ))}
              </div>
            </Section>

            <Section>
              <SectionHeading overline="The fine print" title="What's included & what's not" />
              <div className="mt-8 grid gap-6 md:grid-cols-2">
                <div className="rounded-2xl border border-gold/30 bg-gold/[0.05] p-6 md:p-7">
                  <h4 className="font-serif text-xl font-medium text-charcoal">Included</h4>
                  <ul className="mt-4 space-y-2.5">
                    {ticket.included.map((it, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[15px] text-slatey">
                        <Check size={16} className="mt-0.5 shrink-0 text-gold-dark" strokeWidth={2} />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-2xl border border-stone-line bg-white p-6 md:p-7">
                  <h4 className="font-serif text-xl font-medium text-charcoal">Not included</h4>
                  <ul className="mt-4 space-y-2.5">
                    {ticket.notIncluded.map((it, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[15px] text-slatey">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-stone-line" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Section>

            <Section>
              <SectionHeading overline="Is it right for you?" title="Who this ticket is best for" />
              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {ticket.bestFor.map((b, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 rounded-xl border border-stone-line bg-white px-5 py-4 text-[15px] text-charcoal"
                  >
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold-dark">
                      <Check size={13} strokeWidth={2.5} />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            </Section>

            <Section>
              <SectionHeading overline="Visitor tips" title="Make the most of your visit" />
              <div className="mt-8 space-y-3">
                {ticket.tips.map((t, i) => (
                  <div
                    key={i}
                    className="flex gap-4 rounded-xl border border-stone-line bg-white p-5"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-charcoal text-sm font-medium text-gold">
                      {i + 1}
                    </span>
                    <p className="text-[15px] leading-relaxed font-light text-slatey">{t}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section>
              <AlertBox tone="warning" title="Important information">
                <ul className="mt-2 space-y-2">
                  {ticket.importantInfo.map((info, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-[15px] font-light text-slatey">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-roman-red/60" />
                      {info}
                    </li>
                  ))}
                </ul>
              </AlertBox>
            </Section>

            <Section>
              <SectionHeading overline="Questions answered" title="Frequently asked questions" />
              <div className="mt-6">
                <FAQAccordion items={ticket.faqs} />
              </div>
            </Section>
          </div>

          {/* Sticky sidebar */}
          <aside className="lg:relative">
            <div className="space-y-8 lg:sticky lg:top-24">
              <StickyBookingCard ticket={ticket} />
              <RelatedLinks title="Plan your visit" links={ticket.relatedPlanning} testId="ticket-related-planning" />
            </div>
          </aside>
        </div>
      </div>

      <RelatedTickets
        slugs={ticket.related}
        overline="You might also like"
        title="Related tickets"
        description="Compare other ways to experience the Colosseum and find your perfect fit."
      />

      <div className="container-wide pb-20">
        <CTASection
          title={`Ready to book your ${ticket.shortName || ticket.name}?`}
          text="Secure your timed slot now through our trusted partner GetYourGuide — free cancellation available on most options."
          image={ticket.heroImage}
          bookLabel="Book on GetYourGuide"
        />
      </div>
    </>
  );
}
